// This hook is used to update the chart data when the source data changes
// - API response is passed in as dataRaw when updated
// - Returns formatted data collection with other data points needed for the
//   Recharts tooltip and legend components

import { useContext, useEffect, useState } from 'react';
import * as R from 'ramda';

import { BudgetChart, Chart } from '@atom/types/budget';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailContext from '../BudgetDetailContext';

import { getMonthOnly, GroupKeys } from './expendituresChartUtils';

export const usePrepExpendituresChartData = (budgetsRaw: BudgetChart[]) => {
  const { budget } = useContext(BudgetDetailContext);

  const [dataFields, setDataFields] = useState<string[]>([]);
  const [actualFields, setActualFields] = useState<string[]>([]);
  const [futureFixedFields, setFutureFixedFields] = useState<string[]>([]);
  const [remainingFields, setRemainingFields] = useState<string[]>([]);
  const [futureFixedRemainingFields, setFutureFixedRemainingFields] = useState<
    string[]
  >([]);

  const [maxVal, setMaxVal] = useState<number>(0);
  const [minVal, setMinVal] = useState<number>(0);

  const [dataIsEmpty, setDataIsEmpty] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (isNilOrEmpty(budgetsRaw)) {
      return;
    }

    // Get current budget in budgetsRaw
    // - Use current budget chart months to dictate range
    // - Months outside current budget range are ignored
    // - Months in comparison budgets that have no data will set both values to`0`

    const currentBudgetChart = budgetsRaw.find(
      ({ budgetId }) => budgetId === budget.id,
    );

    if (isNilOrEmpty(currentBudgetChart)) {
      return;
    }

    // Move current budget in data to first position
    const budgetsSorted = R.move(
      budgetsRaw.indexOf(currentBudgetChart),
      0,
      budgetsRaw,
    );

    // Adding initial unnamed columns on the X-axis
    const startColumn = { name: '' };
    const endColumn = { name: ' ' };

    // Building each data column based on the first budget charts collection
    const dataPrepped = currentBudgetChart.charts.reduce((acc, next, idx) => {
      const nextMonthName = next.month;
      const nextMonth = {
        name: nextMonthName,
      };
      budgetsSorted.forEach(budgetChart => {
        const getKey = (group: GroupKeys) =>
          `${budgetChart.budgetName} ${group}`;

        const matchMonth: Chart = budgetChart.charts.find(
          ({ month }) => getMonthOnly(month) === getMonthOnly(nextMonthName),
        );
        const hasFixedCost = matchMonth?.fixedCost > 0;

        // Track first fixed cost to include remaining line
        const firstFixedCost =
          hasFixedCost &&
          R.isNil(R.last(acc)[getKey(GroupKeys.FUTURE_FIXED_REMAINING)]);

        nextMonth[getKey(GroupKeys.ACTUAL)] = matchMonth?.actualCost || 0;
        nextMonth[getKey(GroupKeys.FUTURE_FIXED)] = matchMonth?.fixedCost || 0;

        // Remaining line will be in future data if fixed cost exists
        nextMonth[getKey(GroupKeys.REMAINING)] =
          firstFixedCost || !hasFixedCost
            ? R.pathOr(null, ['totalRemaining'], matchMonth)
            : null;

        nextMonth[getKey(GroupKeys.FUTURE_FIXED_REMAINING)] = hasFixedCost
          ? R.pathOr(null, ['totalRemaining'], matchMonth)
          : null;

        // Populate startColumn for the remaining line point on the Y-axis
        if (idx === 0) {
          startColumn[getKey(GroupKeys.ACTUAL)] = 0;
          startColumn[getKey(GroupKeys.FUTURE_FIXED)] = 0;
          startColumn[getKey(GroupKeys.REMAINING)] = budgetChart.totalBudget;
          startColumn[getKey(GroupKeys.FUTURE_FIXED_REMAINING)] = null;
        }

        // Populate endColumn to display the last month column inside the chart
        // - this is to offset the scale="point" prop on <XAxis />
        if (currentBudgetChart.charts.length - 1) {
          endColumn[getKey(GroupKeys.ACTUAL)] = 0;
          endColumn[getKey(GroupKeys.FUTURE_FIXED)] = 0;
          endColumn[getKey(GroupKeys.REMAINING)] = null;
          endColumn[getKey(GroupKeys.FUTURE_FIXED_REMAINING)] = null;
        }
      });

      // return start & end columns on first and last iteration
      switch (true) {
        case idx === 0:
          return [...acc, startColumn, nextMonth];
        case idx === currentBudgetChart.charts.length - 1:
          return [...acc, nextMonth, endColumn];
        default:
          return [...acc, nextMonth];
      }
    }, []);

    setData(dataPrepped);

    const dataFieldsCurrent = R.without(
      ['name'],
      R.keys(R.pathOr({}, [0], dataPrepped)),
    );

    setDataFields(dataFieldsCurrent);

    setActualFields(
      dataFieldsCurrent.filter(field => R.endsWith(GroupKeys.ACTUAL, field)),
    );

    setFutureFixedFields(
      dataFieldsCurrent.filter(field =>
        R.endsWith(GroupKeys.FUTURE_FIXED, field),
      ),
    );

    setRemainingFields(
      dataFieldsCurrent.filter(field => R.endsWith(GroupKeys.REMAINING, field)),
    );

    setFutureFixedRemainingFields(
      dataFieldsCurrent.filter(field =>
        R.endsWith(GroupKeys.FUTURE_FIXED_REMAINING, field),
      ),
    );

    const getValues = (field: string): number[] =>
      dataPrepped.map(item => item[field]);

    const allVals: number[] = dataFieldsCurrent.reduce(
      (acc, field): number[] => [...acc, ...getValues(field)],
      [],
    );

    // dataIsEmpty not currently in use, but may come up soon
    const uniqueValues = R.uniq(allVals);
    setDataIsEmpty(uniqueValues.length === 1 && uniqueValues[0] === 0);

    setMaxVal(Math.max(...allVals));
    setMinVal(Math.min(...allVals));
  }, [budgetsRaw]);

  return {
    dataFields,
    actualFields,
    futureFixedFields,
    remainingFields,
    futureFixedRemainingFields,
    maxVal,
    minVal,
    data,
    dataIsEmpty,
  };
};
